import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { completeGoogleSignIn } from '../lib/auth-flow.js'
import { isClassroomConnectCallback } from '../lib/classroom-connect.js'
import ClassroomConnectCallbackPage from './teacher/ClassroomConnectCallbackPage'

type Phase = 'working' | 'error'

export default function CallbackPage(): JSX.Element {
  const navigate = useNavigate()
  const isClassroomCallback = isClassroomConnectCallback()
  const [phase, setPhase] = useState<Phase>('working')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Guard against React 18 StrictMode running this effect twice in dev,
  // which would consume the one-shot PKCE state on the first pass and
  // fail the second with a spurious "State mismatch" error.
  const ranRef = useRef(false)

  useEffect(() => {
    if (isClassroomCallback) return
    if (ranRef.current) return
    ranRef.current = true
    void (async () => {
      try {
        const { returnTo } = await completeGoogleSignIn()
        navigate(returnTo, { replace: true })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-in failed'
        setPhase('error')
        setErrorMessage(message)
        setTimeout(() => {
          navigate(`/?error=${encodeURIComponent(message)}`, { replace: true })
        }, 50)
      }
    })()
  }, [isClassroomCallback, navigate])

  if (isClassroomCallback) return <ClassroomConnectCallbackPage />

  return (
    <main id="main-content">
      <h1>Signing you in…</h1>
      {phase === 'working' && (
        <p role="status" aria-live="polite">
          Verifying your school account. This usually takes a second.
        </p>
      )}
      {phase === 'error' && (
        <p role="alert">{errorMessage ?? 'Sign-in failed.'} Returning to the home page…</p>
      )}
    </main>
  )
}
